import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { BadgeAlert, IdCard, Files, Droplets, Clock4, ShieldAlert } from "lucide-react";

const ITEMS = [
  {
    icon: IdCard,
    title: "Carry valid government ID for pregnancy sonography",
    body: "As per Indian regulations, a government photo ID (Aadhaar / PAN / Driving Licence / Passport) is mandatory for any pregnancy ultrasound. Please carry the original.",
  },
  {
    icon: Files,
    title: "Bring previous reports if available",
    body: "Earlier scans, prescriptions, blood reports or referral letters help your radiologist provide a more accurate, comparative report.",
  },
  {
    icon: Droplets,
    title: "Drink water before scans if instructed",
    body: "Some abdominal and pelvic ultrasounds require a full bladder. If your doctor or our staff has instructed you to drink water, please follow the timing carefully.",
  },
  {
    icon: Clock4,
    title: "Arrive 10 minutes early",
    body: "Arriving a little early gives you time to register, complete formalities and start your scan stress-free at your booked slot.",
  },
  {
    icon: ShieldAlert,
    title: "Gender determination is strictly prohibited (PCPNDT Act)",
    body: "Under the Pre-Conception and Pre-Natal Diagnostic Techniques Act, sex determination of the foetus is illegal. Nidan strictly complies with the PCPNDT Act — please do not request it.",
  },
];

export default function BeforeVisit() {
  return (
    <section
      id="before-visit"
      data-testid="before-visit-section"
      className="py-20 lg:py-28 bg-[#f8fafc]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-5">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0099CC]">
              Before Your Visit
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
              Important <span className="text-[#0099CC]">Patient Information</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              A quick read to make your visit smooth, safe and stress-free.
            </p>

            <div
              className="mt-7 inline-flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-100"
              data-testid="pcpndt-notice"
            >
              <BadgeAlert className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-900 leading-relaxed">
                <strong className="font-semibold">PCPNDT Compliant:</strong> Foetal sex
                determination is a punishable offence under Indian law. Nidan does not
                conduct or disclose any such tests.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Accordion
              type="single"
              collapsible
              defaultValue="item-0"
              className="space-y-3"
              data-testid="before-visit-accordion"
            >
              {ITEMS.map((it, idx) => (
                <AccordionItem
                  key={it.title}
                  value={`item-${idx}`}
                  data-testid={`accordion-item-${idx}`}
                  className="bg-white rounded-2xl border border-slate-100 px-5 sm:px-6 shadow-[0_4px_20px_rgba(15,23,42,0.03)] data-[state=open]:shadow-[0_18px_40px_rgba(15,23,42,0.07)] transition-shadow"
                >
                  <AccordionTrigger className="hover:no-underline py-5">
                    <span className="flex items-center gap-3 text-left">
                      <span className="w-10 h-10 rounded-xl bg-[#00BFFF]/10 text-[#0099CC] flex items-center justify-center shrink-0">
                        <it.icon className="w-5 h-5" strokeWidth={2.2} />
                      </span>
                      <span className="text-base sm:text-lg font-semibold text-slate-900">
                        {it.title}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-slate-600 leading-relaxed pl-13 pb-5">
                    {it.body}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
